import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { BlockLink } from "@/components/DrilldownLink";
import {
  DISTRICT_HERO_WARNING_CONFIG,
  blockWarningStatusLabel,
  getDistrictHeroWarningBlocks,
} from "@/utils/districtHeroWarningsDerived";
import "./UsmRiskSchoolsModal.css";
import "./DistrictHeroWarningModal.css";

const API = `${getBackendUrl()}/api`;
const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const pct = (v) => (typeof v === "number" ? `${v.toFixed(1)}%` : "—");

const DistrictHeroWarningModal = ({
  open,
  warningId,
  onClose,
  district,
  blocks = [],
  scopeLabel = "District scope",
}) => {
  const navigate = useNavigate();
  const config = warningId ? DISTRICT_HERO_WARNING_CONFIG[warningId] : null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schoolsByBlock, setSchoolsByBlock] = useState({});

  const targetBlocks = useMemo(() => {
    if (!config || !district) return [];
    return getDistrictHeroWarningBlocks(warningId, blocks, district);
  }, [config, warningId, blocks, district]);

  useEffect(() => {
    if (!open || !warningId || !config || targetBlocks.length === 0) {
      setSchoolsByBlock({});
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(
      targetBlocks.map((block) =>
        axios
          .get(`${API}/blocks/${block.block_code}/schools`, {
            params: { limit: 500, sort_by: "shi_score", sort_order: "asc" },
            headers: { "x-skip-scope": "true" },
          })
          .then((res) => [block.block_code, Array.isArray(res.data) ? res.data : []])
          .catch(() => [block.block_code, []]),
      ),
    )
      .then((entries) => {
        if (cancelled) return;
        setSchoolsByBlock(Object.fromEntries(entries));
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load school-level drivers for at-risk blocks.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, warningId, config, targetBlocks]);

  if (!open || !warningId || !config || !district) return null;

  const summary = config.summary(district, targetBlocks);

  return createPortal(
    <div className="usm-risk-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="usm-risk-modal usm-risk-modal-wide dhw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dhw-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="usm-risk-modal-head">
          <div>
            <p className="usm-risk-modal-eyebrow">District hero drill-down · {scopeLabel}</p>
            <h3 id="dhw-modal-title">{config.title}</h3>
            <p className="usm-risk-modal-sub">{summary}</p>
          </div>
          <button type="button" className="usm-risk-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="usm-risk-modal-toolbar usm-risk-modal-toolbar-filters">
          <span className="usm-risk-modal-count">
            Showing <b>{targetBlocks.length}</b> block{targetBlocks.length !== 1 ? "s" : ""}
            {loading ? " · loading school drivers…" : null}
          </span>
        </div>

        <div className="dhw-modal-body">
          {error ? <div className="usm-risk-modal-error">{error}</div> : null}
          {targetBlocks.length === 0 ? (
            <div className="usm-risk-modal-empty">No blocks match this warning in the current district scope.</div>
          ) : (
            targetBlocks.map((block) => {
              const schools = schoolsByBlock[block.block_code] || [];
              const driverSchools = config.selectSchools(schools, block, district);
              const status = blockWarningStatusLabel(block);
              const statusClass =
                block.rag_status === "red" ? "red" : block.rag_status === "amber" ? "amber" : "";

              return (
                <section className="dhw-block-section" key={block.block_code || block.block_name}>
                  <div className="dhw-block-head">
                    <div>
                      <h4>
                        <BlockLink blockCode={block.block_code} blockName={block.block_name}>
                          {block.block_name}
                        </BlockLink>
                      </h4>
                    </div>
                    <div className="dhw-block-metrics">
                      <span className={`dhw-metric-pill ${statusClass}`}>{status}</span>
                      <span className="dhw-metric-pill">OSH {typeof block.shi_score === "number" ? block.shi_score.toFixed(1) : "—"}</span>
                      <span className="dhw-metric-pill">Aadhaar {pct(block.aadhaar_percentage)}</span>
                      <span className="dhw-metric-pill amber">APAAR {pct(block.apaar_percentage)}</span>
                    </div>
                  </div>

                  <div className="dhw-block-copy">
                    <p>
                      <strong>Why at risk:</strong> {config.blockReason(block, district)}
                    </p>
                    <p>
                      <strong>How to solve:</strong> {config.blockResolution(block, district)}
                    </p>
                  </div>

                  <div className="dhw-schools-wrap">
                    <h5>Schools driving risk ({driverSchools.length})</h5>
                    {loading && driverSchools.length === 0 ? (
                      <div className="usm-risk-modal-loading">Loading schools…</div>
                    ) : driverSchools.length === 0 ? (
                      <div className="dhw-empty-schools">No school-level drivers found for this block.</div>
                    ) : (
                      <table className="dhw-school-table">
                        <thead>
                          <tr>
                            <th>School</th>
                            <th>UDISE</th>
                            <th>Students</th>
                            <th>Teachers</th>
                            <th>Aadhaar</th>
                            <th>APAAR</th>
                            <th>OSH</th>
                            <th>Why flagged</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {driverSchools.map((school) => (
                            <tr
                              key={school.udise_code}
                              onClick={() => {
                                onClose();
                                navigate(`/school/${school.udise_code}`);
                              }}
                            >
                              <td>{school.school_name}</td>
                              <td>{school.udise_code}</td>
                              <td>{fmt(school.total_students)}</td>
                              <td>{fmt(school.total_teachers)}</td>
                              <td>{pct(school.aadhaar_percentage)}</td>
                              <td>{pct(school.apaar_percentage)}</td>
                              <td>{typeof school.shi_score === "number" ? school.shi_score.toFixed(1) : "—"}</td>
                              <td>{config.schoolReason(school, block, district)}</td>
                              <td>{config.schoolAction(school, block, district)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DistrictHeroWarningModal;
